"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const experiences = [{
  id: "exp-1",
  title: "Private Aegean Charter",
  description: "Sail the crystalline waters of the Cyclades in a fully crewed luxury catamaran. Discover hidden coves, ancient ruins, and dine under the stars in absolute privacy.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/aegean_charter_yacht.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/aegean_charter_yacht.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/aegean_charter_yacht.png-wvc-sizes",
    alt: "Luxury yacht in the Aegean Sea"
  },
  link: "/GreecePage"
}, {
  id: "exp-2",
  title: "Andean Cloud Forest Retreat",
  description: "Immerse yourself in the unparalleled biodiversity of Colombia. Stay in an exclusive, architecturally stunning eco-lodge suspended high above the vibrant canopy.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/andean_cloud_forest.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/andean_cloud_forest.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/andean_cloud_forest.png-wvc-sizes",
    alt: "Eco-lodge in the Colombian cloud forest"
  },
  link: "/ColombiaPage"
}, {
  id: "exp-3",
  title: "Nile Antiquities Voyage",
  description: "A meticulously curated journey through ancient history on a private dahabiya. Enjoy exclusive, after-hours access to monumental archaeological sites.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/nile_dahabiya_voyage.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/nile_dahabiya_voyage.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/nile_dahabiya_voyage.png-wvc-sizes",
    alt: "Traditional dahabiya sailing on the Nile"
  },
  link: "/EgyptPage"
}];
const ExperienceCard = ({
  experience,
  index
}: {
  experience: (typeof experiences)[0];
  index: number;
}) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[20px]"} ${index === 1 ? "md:mt-16" : ""}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="group h-full overflow-hidden border-none bg-foreground/95 backdrop-blur-[20px] shadow-2xl rounded-2xl flex flex-col">
        <div className="relative overflow-hidden">
          <AspectRatio ratio={4 / 5}>
            <img src={experience.image.src} data-wvc-srcset={experience.image.srcset} data-wvc-sizes={experience.image.sizes} alt={experience.image.alt} loading="lazy" className="object-cover w-full h-full transition-transform duration-700 ease-in-out group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent opacity-60" />
          </AspectRatio>
        </div>
        
        <CardHeader className="pt-8 pb-4 relative z-10">
          <CardTitle className="font-default text-2xl font-semibold text-secondary tracking-wide">
            {experience.title}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="flex-grow relative z-10">
          <CardDescription className="font-sans text-muted/90 text-base leading-relaxed">
            {experience.description}
          </CardDescription>
        </CardContent>
        
        <CardFooter className="pb-8 pt-4 relative z-10">
          <Button asChild variant="link" className="p-0 h-auto text-secondary hover:text-secondary/80 font-sans tracking-wide group/btn">
            <Link to={experience.link}>
              Discover More
              <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>;
};
export default function ExclusiveExperiences() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return;
}